import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';

import { TOKENS } from '../board/ganttConfig';
import { Swatch } from '../InkStockView';
import { getInkForecast } from '../productionApi';
import { productionKeys } from '../queryKeys';
import ProjectionPicker, { Selection } from './ProjectionPicker';
import { ForecastDropZone, ForecastImportButton } from './ForecastUpload';
import {
  ColMode, ForecastColumn, ForecastNode, HORIZON, InkForecast,
  aggregate, balanceOf, buildColumns, buildTree, decodeShareState,
  coverageSummary, encodeShareState, fmtKg, fmtSheets, grandTotalKg,
  leafCoverage, leafFullMeta, shortInks,
} from './inkForecastModel';

/**
 * Ink demand forecast — the interactive report.
 *
 * Answers "why is this number what it is". Five levels deep, collapsible in both
 * directions (rows AND the time axis), with a shareable expand state in the URL.
 * The buyer's counterpart is InkOrderDocumentView, which reads the same figures
 * so screen and paper cannot disagree.
 *
 * This is the ink analogue of the raw-material (board) forecast the business
 * already reads fluently, so the register is deliberately the same: tabular,
 * monospaced figures, hairline rules, high density. It is NOT a dashboard —
 * no tiles, no charts. Colour is functional only (negatives, ink swatches, the
 * Abaris accent on active controls).
 *
 * THE INTERACTION MODEL — "pinned spine"
 *
 * Series and ink rows are a permanent skeleton; the ink row is the click target.
 * Expansion is NON-EXCLUSIVE on purpose: a planner holds three problem inks open
 * at once and compares them, which an accordion would forbid.
 *
 * Collapsing changes what you SEE, never what it sums to. The Σ in the status
 * bar is that guarantee made visible — see inkForecastModel, which is the only
 * place the arithmetic lives, and its test.
 */

const ROW_H = 20;
const SUMMARY_ROW_H = 21;
const SUMMARY_H = SUMMARY_ROW_H * 3;
const INDENT = 16;
const LABEL_COL_W = 430;
const OPENING_COL_W = 74;
const CONTEXT_H = 21;

const MONO = TOKENS.monoFont;
const RULE = TOKENS.line;
const EMPHASIS_RULE = 'rgba(38,32,27,0.30)';
const ZERO_INK = 'rgba(38,32,27,0.22)';
const WIDE_FILL = 'rgba(38,32,27,0.045)';
const WIDE_FILL_STRONG = TOKENS.lineSoft;
const WINDOW_TINT = 'rgba(203,96,21,0.10)';
const BG_SERIES = TOKENS.lineSoft;
const BG_INK = 'rgba(38,32,27,0.04)';
const BG_SHEETS = 'rgba(38,32,27,0.02)';

type RowKind = 'series' | 'group' | 'leaf' | 'sheets' | 'summary';

interface Row {
  key: string;
  node: ForecastNode;
  indent: number;
  kind: RowKind;
  open: boolean;
  height: number;
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

const figSx = {
  fontFamily: MONO, fontSize: '11px', textAlign: 'right' as const,
  px: '8px', whiteSpace: 'nowrap' as const, fontVariantNumeric: 'tabular-nums',
};

interface FigRowOpts {
  bold?: boolean;
  dim?: boolean;
  /** Wide columns get the heavier fill on series / ink rows. */
  strong?: boolean;
  signed?: boolean;
  sheets?: boolean;
  /** Only the Balance row uses the Opening column; elsewhere it shows a dimmed dot. */
  openingVal?: number;
}

function FigRow({ values, columns, opts = {} }: {
  values: number[]; columns: ForecastColumn[]; opts?: FigRowOpts;
}) {
  const fmt = opts.sheets ? fmtSheets : fmtKg;
  return (
    <>
      <Box sx={{
        ...figSx,
        color: opts.openingVal !== undefined
          ? (opts.openingVal < 0 ? TOKENS.bad : TOKENS.ink)
          : ZERO_INK,
      }}>
        {opts.openingVal !== undefined ? fmtKg(opts.openingVal) : '·'}
      </Box>
      {columns.map((c) => {
        const v = aggregate(values, c.indices);
        const negative = !!opts.signed && v < 0;
        return (
          <Box key={c.key} sx={{
            ...figSx,
            fontWeight: opts.bold ? 600 : 400,
            color: negative ? TOKENS.bad
              : v ? (opts.dim ? TOKENS.ink2 : TOKENS.ink) : ZERO_INK,
            bgcolor: c.wide ? (opts.strong ? WIDE_FILL_STRONG : WIDE_FILL) : 'transparent',
            borderLeft: c.wide ? `1px solid ${RULE}` : 'none',
          }}>
            {fmt(v)}
          </Box>
        );
      })}
    </>
  );
}

/** One CSS grid per row, all sharing the `--cols` custom property on the wrapper. */
const gridSx = { display: 'grid', gridTemplateColumns: 'var(--cols)', alignItems: 'center' };

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

function CtlButton({ label, active, onClick, title }: {
  label: string; active?: boolean; onClick: () => void; title?: string;
}) {
  const btn = (
    <Box component="button" type="button" onClick={onClick} sx={{
      font: 'inherit', fontSize: 12, fontWeight: active ? 600 : 500,
      px: '10px', py: '3px', borderRadius: `${TOKENS.radiusChip}px`, cursor: 'pointer',
      border: `1px solid ${active ? TOKENS.accent : RULE}`,
      bgcolor: active ? TOKENS.accent : TOKENS.panel,
      color: active ? '#fff' : TOKENS.ink2,
      boxShadow: TOKENS.shadowSm, whiteSpace: 'nowrap',
      '&:hover': { borderColor: TOKENS.accent, color: active ? '#fff' : TOKENS.accent },
    }}>
      {label}
    </Box>
  );
  return title ? <Tooltip title={title}>{btn}</Tooltip> : btn;
}

function CtlGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Box component="span" sx={{
        fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: TOKENS.ink3,
      }}>
        {title}
      </Box>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export default function InkForecastView() {
  const initial = useMemo(() => decodeShareState(window.location.hash), []);
  const [colMode, setColMode] = useState<ColMode>(initial.colMode ?? 'month');
  const [winStart, setWinStart] = useState(initial.winStart ?? 0);
  const [winEnd, setWinEnd] = useState(initial.winEnd ?? 5);
  const [open, setOpen] = useState<Record<string, boolean>>(initial.open ?? {});
  const [qOpen, setQOpen] = useState<Record<number, boolean>>(initial.qOpen ?? {});
  const [drag, setDrag] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [selection, setSelection] = useState<Selection>(initial.projections ?? null);
  const [scrollTop, setScrollTop] = useState(0);
  const [headerH, setHeaderH] = useState(40);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // null selection = let the server pick (newest per customer, every customer).
  const selParam = selection === null ? undefined : selection.join(',');
  // An EMPTY list is not the same as no list. Both stringify to something falsy,
  // so asking for none used to arrive at the server as no `projections` param at
  // all — which is the documented request for the newest projection of EVERY
  // customer. "None" therefore showed everything. The distinction cannot be made
  // on the wire, so it is made here: an explicit empty selection never asks.
  const selectedNone = Array.isArray(selection) && selection.length === 0;
  const { data, isLoading, error } = useQuery({
    queryKey: productionKeys.inkForecast(selParam),
    queryFn: () => getInkForecast(selParam),
    staleTime: 5 * 60_000,
    enabled: !selectedNone,
  });

  const forecast = data as InkForecast | undefined;
  const months = useMemo(() => forecast?.months ?? [], [forecast]);
  const tree = useMemo(() => (forecast ? buildTree(forecast) : []), [forecast]);

  // --- shareable state ------------------------------------------------------
  // replaceState, never pushState: this fires on every expand, every column
  // change and every step of a header drag. Pushing would bury the user's real
  // navigation history under hundreds of entries.
  const hash = useMemo(
    () => encodeShareState({
      colMode, winStart, winEnd, open, qOpen,
      projections: selection ?? undefined,
    }),
    [colMode, winStart, winEnd, open, qOpen, selection],
  );
  useEffect(() => {
    try {
      window.history.replaceState(
        null, '', `${window.location.pathname}${window.location.search}#${hash}`,
      );
    } catch { /* history blocked (sandboxed iframe) — the view still works */ }
  }, [hash]);

  // A drag can end anywhere, including outside the header row, so the release
  // is caught on the window rather than on the cell that started it.
  useEffect(() => {
    if (drag === null) return undefined;
    const end = () => setDrag(null);
    window.addEventListener('mouseup', end);
    return () => window.removeEventListener('mouseup', end);
  }, [drag]);

  const columns = useMemo(
    () => (months.length ? buildColumns(months, colMode, winStart, winEnd, qOpen) : []),
    [months, colMode, winStart, winEnd, qOpen],
  );

  // --- row list -------------------------------------------------------------
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const walk = (node: ForecastNode, indent: number) => {
      const isOpen = !!open[node.key];
      out.push({
        key: `${node.key}:g`, node, indent, kind: 'group', open: isOpen, height: ROW_H,
      });
      if (!isOpen) return;
      node.children.forEach((child) => {
        if (child.level === 4) {
          // Every leaf renders TWO rows: the kg figures, and beneath them a
          // lighter "Sheet demand" row carrying the derivation. Both always
          // show — that pair is the audit trail, and the reason the kg above it
          // is trusted rather than taken on faith.
          out.push({ key: `${child.key}:l`, node: child, indent: indent + 1, kind: 'leaf', open: false, height: ROW_H });
          out.push({ key: `${child.key}:s`, node: child, indent: indent + 1, kind: 'sheets', open: false, height: ROW_H });
        } else {
          walk(child, indent + 1);
        }
      });
      if (node.level === 1) {
        out.push({ key: `${node.key}:sum`, node, indent, kind: 'summary', open: true, height: SUMMARY_H });
      }
    };
    tree.forEach((series) => {
      out.push({ key: `${series.key}:s`, node: series, indent: 0, kind: 'series', open: true, height: ROW_H });
      series.children.forEach((ink) => walk(ink, 1));
    });
    return out;
  }, [tree, open]);

  // Cumulative offsets — the virtualiser has its own, but the context band is
  // derived from the SCROLL OFFSET rather than from the render window (a band
  // driven by what happens to be drawn flickers as the overscan shifts).
  const offsets = useMemo(() => {
    const out: number[] = [];
    let acc = 0;
    rows.forEach((r) => { out.push(acc); acc += r.height; });
    return out;
  }, [rows]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // Exact, not estimated: every row is either 20px or a 63px summary block, so
    // there is nothing to measure and no reflow to pay for.
    estimateSize: (i) => rows[i].height,
    overscan: 12,
    // The sticky header sits above the list inside the same scroller.
    paddingStart: 0,
  });

  // The context band pins directly under the column header, and that header
  // changes height between grains (quarter mode adds the +mth buttons). Measuring
  // beats a hard-coded offset, which leaves the band hidden behind the header the
  // moment the header grows.
  //
  // A CALLBACK ref, not an effect: the header does not exist during the loading
  // state (this component returns a spinner before rendering it), so a mount
  // effect measures null once and never runs again — which pinned the band at the
  // initial 40px and tucked it under a 60px quarterly header.
  const observerRef = useRef<ResizeObserver | null>(null);
  const headerRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const measure = () => setHeaderH((prev) => {
      const h = el.offsetHeight;
      return h && Math.abs(h - prev) > 1 ? h : prev;
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    observerRef.current = ro;
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Coarse threshold: the band only changes when you cross an ink boundary, so
    // re-rendering on every pixel would be pure waste.
    setScrollTop((prev) => (Math.abs(prev - el.scrollTop) > 20 ? el.scrollTop : prev));
  }, []);

  // --- derived figures ------------------------------------------------------
  const grandTotal = useMemo(() => grandTotalKg(tree), [tree]);
  const short = useMemo(() => shortInks(tree), [tree]);
  const leafCount = useMemo(
    () => tree.reduce((a, s) => a + s.leafCount, 0), [tree],
  );

  /** The ink the viewport is currently inside — never let a deep row be orphaned. */
  const context = useMemo(() => {
    if (!rows.length) return null;
    const y = Math.max(0, scrollTop - headerH);
    let i = 0;
    while (i + 1 < offsets.length && offsets[i + 1] <= y + CONTEXT_H) i += 1;
    let ink: ForecastNode | null = null;
    let customer: ForecastNode | null = null;
    for (let j = i; j >= 0; j -= 1) {
      const n = rows[j].node;
      if (!customer && n.level === 2) customer = n;
      if (n.level === 1) { ink = n; break; }
    }
    return scrollTop > headerH && ink ? { ink, customer } : null;
  }, [rows, offsets, scrollTop, headerH]);

  // --- handlers -------------------------------------------------------------
  const toggle = useCallback((key: string) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setWindow = useCallback((n: number) => {
    setWinStart(0);
    setWinEnd(Math.min(HORIZON - 1, n - 1));
  }, []);

  const copyLink = useCallback(() => {
    const url = `${window.location.href.split('#')[0]}#${hash}`;
    navigator.clipboard?.writeText(url).catch(() => { /* clipboard denied */ });
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }, [hash]);

  const openShortInks = useCallback(() => {
    const next: Record<string, boolean> = {};
    short.forEach((n) => { next[n.key] = true; });
    setOpen(next);
  }, [short]);

  const orderHref = useMemo(() => {
    // The document inherits the exact projection SET, not just the window —
    // otherwise the paper could be built from a different selection than the
    // screen it was opened from.
    const p = (forecast?.projections ?? [])
      .map((x) => `${x.code}:${x.customer}`).join(',');
    return `/production/ink-order-document#w=${winStart}-${winEnd}`
      + (p ? `&p=${encodeURIComponent(p)}` : '');
  }, [forecast, winStart, winEnd]);

  const gridVars = useMemo(() => ({
    width: 'max-content',
    minWidth: '100%',
    // Wide (window / quarter) columns get more room than months so the buyer's
    // figure reads as the headline it is.
    '--cols': `${LABEL_COL_W}px ${OPENING_COL_W}px ${columns
      .map((c) => (c.wide ? 'minmax(94px,1.3fr)' : 'minmax(62px,1fr)')).join(' ')}`,
  }), [columns]);

  // --- render ---------------------------------------------------------------
  // Before the loading and error branches: with the query disabled there is no
  // data and no error, which would otherwise fall through to the "nothing loaded"
  // state and wrongly offer an import for a report that is merely deselected.
  // The picker comes WITH this state — an empty screen that removed the only
  // control that can refill it would be a dead end.
  if (selectedNone) {
    return (
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <Box sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Ink demand forecast</Box>
        <Box sx={{ fontSize: 12.5, color: TOKENS.ink2, lineHeight: 1.6, mb: 2 }}>
          No projections selected. Choose one or more below, or pick
          {' '}<b>Newest for all</b> to go back to the default.
        </Box>
        <ProjectionPicker active={[]} selection={selection} onChange={setSelection} />
      </Box>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={22} sx={{ color: TOKENS.accent }} />
      </Box>
    );
  }
  if (error || !forecast) {
    const message = (error as any)?.response?.data?.error
      ?? (error as Error)?.message ?? 'No forecast available.';
    // The drop zone lives IN the empty state rather than behind a button: with
    // nothing loaded, importing is the only useful action on this screen, and a
    // dead end that merely explains the problem is what made the report
    // unusable to the people who actually receive the PDFs.
    return (
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <Box sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Ink demand forecast</Box>
        <Box sx={{ fontSize: 12.5, color: TOKENS.ink2, lineHeight: 1.6, mb: 2 }}>{message}</Box>
        <ForecastDropZone />
      </Box>
    );
  }

  // The report names what it was built from. A forecast assembled from a
  // six-week-old projection for one customer is a materially different number
  // from one built on this week's, so the count, the customers and the horizon
  // are stated rather than presented as an anonymous total.
  const projections = forecast.projections;
  const stale = projections.filter((p) => p.dropped_sheets_before_horizon > 0);
  const subline = [
    `Customer: ${forecast.customers.length === 1 ? forecast.customers[0]
      : `ALL (${forecast.customers.length})`}`,
    `Projections: ${projections.length}`,
    `Horizon: ${(months[0] || '').slice(0, 3)}–${(months[HORIZON - 1] || '').slice(0, 3)}`,
    'Driver: sheet demand from the Prism RM board forecast',
    'Units: sheets / kg',
  ].join(' · ');
  const coverage = coverageSummary(forecast);
  const projectionTitle = projections
    .map((p) => `${p.customer} ${p.code}${p.dropped_sheets_before_horizon
      ? ` (${Math.round(p.dropped_sheets_before_horizon).toLocaleString()} sheets before the horizon, excluded)` : ''}`)
    .join('\n');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: TOKENS.bg }}>
      {/* --- header bar --- */}
      <Box sx={{
        borderBottom: `1px solid ${RULE}`, bgcolor: TOKENS.panel, boxShadow: TOKENS.shadowSm,
        px: 2, pt: '10px', pb: '8px', display: 'flex', alignItems: 'flex-end',
        gap: 2.5, flexWrap: 'wrap', flex: '0 0 auto',
      }}>
        <Box>
          <Box sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink }}>
            Ink demand forecast — by ink code
          </Box>
          <Box title={projectionTitle} sx={{ fontSize: 12, color: TOKENS.ink3, mt: '2px', cursor: 'help' }}>
            {subline}
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CtlButton label={copied ? 'Link copied' : 'Copy link'} onClick={copyLink} />
          <Box
            component="a" href={orderHref} target="_blank" rel="noreferrer"
            sx={{
              fontSize: 12, fontWeight: 600, px: '12px', py: '5px',
              borderRadius: `${TOKENS.radiusChip}px`, textDecoration: 'none',
              border: `1px solid ${TOKENS.accent}`, bgcolor: TOKENS.accent, color: '#fff',
              boxShadow: TOKENS.shadowSm, whiteSpace: 'nowrap',
              '&:hover': { bgcolor: TOKENS.accentStrong, borderColor: TOKENS.accentStrong },
            }}
          >
            Order document
          </Box>
        </Box>
      </Box>

      {/* --- control bar --- */}
      <Box sx={{
        display: 'flex', gap: '22px', flexWrap: 'wrap', alignItems: 'center',
        px: 2, py: 1, borderBottom: `1px solid ${RULE}`, bgcolor: TOKENS.bg, flex: '0 0 auto',
      }}>
        <CtlGroup title="Columns">
          <CtlButton label="Monthly" active={colMode === 'month'} onClick={() => setColMode('month')} />
          <CtlButton label="Quarterly" active={colMode === 'quarter'} onClick={() => setColMode('quarter')} />
        </CtlGroup>
        <CtlGroup title="Order window">
          <CtlButton label="3 mth" active={winStart === 0 && winEnd === 2} onClick={() => setWindow(3)} />
          <CtlButton label="6 mth" active={winStart === 0 && winEnd === 5} onClick={() => setWindow(6)} />
          <CtlButton label="12 mth" active={winStart === 0 && winEnd === 11} onClick={() => setWindow(12)} />
          <Box component="span" sx={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, color: TOKENS.accent }}>
            {(months[winStart] || '').slice(0, 3)}–{(months[winEnd] || '').slice(0, 3)}
          </Box>
          <Box component="span" sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>
            or drag across the month headers
          </Box>
        </CtlGroup>
        <CtlGroup title="Projections">
          <ProjectionPicker
            active={forecast.projections}
            selection={selection}
            onChange={setSelection}
          />
          <ForecastImportButton ButtonComponent={CtlButton} />
        </CtlGroup>
        <CtlGroup title="Rows">
          <CtlButton label="Collapse all" onClick={() => setOpen({})} />
          <CtlButton
            label="Open short inks"
            onClick={openShortInks}
            title="Expands every ink whose projected balance goes negative at any point in the horizon"
          />
        </CtlGroup>
      </Box>

      {/* --- coverage band ---
          A forecast that silently omits a fifth of its own demand is worse than
          no forecast, because it looks complete. This states the shortfall as a
          PERCENTAGE OF DEMAND rather than a count of SKUs — 183 SKUs conveys
          nothing about magnitude — and splits it by cause, because the causes
          have different owners and different fixes. Persistent, not a tooltip:
          it is a caveat on every figure above and below it. */}
      {coverage.uncostedSheets > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap',
          px: 2, py: '5px', flex: '0 0 auto', fontSize: 11.5,
          borderBottom: `1px solid ${RULE}`, borderLeft: `3px solid ${TOKENS.warn}`,
          bgcolor: 'rgba(180,120,20,0.06)', color: TOKENS.ink2,
        }}>
          <Box component="span" sx={{ fontWeight: 700, color: TOKENS.warn }}>
            {coverage.uncostedPct.toFixed(1)}% of forecast demand is not costed
          </Box>
          <Box component="span" sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
            {fmtSheets(coverage.uncostedSheets)} of {fmtSheets(coverage.totalSheets)} sheets
          </Box>
          <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>
          {coverage.buckets.map((b) => (
            <Tooltip key={b.kind} title={
              <Box sx={{ fontSize: 11 }}>
                <Box sx={{ fontWeight: 700, mb: 0.5 }}>{b.action}</Box>
                {forecast.excluded.filter((e) => e.kind === b.kind).slice(0, 10).map((e) => (
                  <Box key={`${e.customer}:${e.sku}:${e.board}`}>
                    {e.customer} {e.sku} · {fmtSheets(e.sheets)} sh
                  </Box>
                ))}
                {b.lines > 10 && <Box>…and {b.lines - 10} more</Box>}
              </Box>
            }>
              <Box component="span" sx={{
                cursor: 'help', whiteSpace: 'nowrap',
                // A permanent gap is stated plainly; a fixable one is underlined
                // as the work item it is.
                textDecoration: b.nature === 'fixable' ? 'underline dotted' : 'none',
                textUnderlineOffset: '3px',
                color: b.nature === 'fixable' ? TOKENS.ink2 : TOKENS.ink3,
              }}>
                <Box component="span" sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtSheets(b.sheets)}
                </Box>
                {' '}{b.label} ({b.lines})
              </Box>
            </Tooltip>
          ))}
          {coverage.masterGaps > 0 && (
            <Tooltip title={
              <Box sx={{ fontSize: 11 }}>
                {forecast.master_gaps.slice(0, 10).map((g) => (
                  <Box key={`${g.customer}:${g.sku}`}>
                    {g.customer} {g.sku} · {g.reason}
                  </Box>
                ))}
              </Box>
            }>
              <Box component="span" sx={{ color: TOKENS.ink3, cursor: 'help', whiteSpace: 'nowrap' }}>
                · {coverage.masterGaps} counted but missing a die
              </Box>
            </Tooltip>
          )}
        </Box>
      )}

      {/* --- scroll body --- */}
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: TOKENS.panel }}
      >
        <Box sx={gridVars}>
          <Box ref={headerRef} sx={{
            position: 'sticky', top: 0, zIndex: 4, bgcolor: TOKENS.bg,
            borderBottom: `1px solid ${RULE}`,
          }}>
            <ColumnHeader
              columns={columns} winStart={winStart} winEnd={winEnd} qOpen={qOpen}
              onQuarter={(q) => setQOpen((prev) => ({ ...prev, [q]: !prev[q] }))}
              onDragStart={(m) => { setDrag(m); setWinStart(m); setWinEnd(m); }}
              onDragOver={(m) => {
                if (drag === null) return;
                setWinStart(Math.min(drag, m));
                setWinEnd(Math.max(drag, m));
              }}
            />
          </Box>

          {context && (
            <ContextBand
              ink={context.ink} customer={context.customer}
              columns={columns} top={headerH}
            />
          )}

          <Box sx={{ position: 'relative', height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((v) => {
              const row = rows[v.index];
              return (
                <Box
                  key={row.key}
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
                  style={{ transform: `translateY(${v.start}px)`, height: v.size }}
                >
                  <ForecastRow row={row} columns={columns} onToggle={toggle} />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* --- status bar --- */}
      <Box sx={{
        borderTop: `1px solid ${RULE}`, bgcolor: TOKENS.panel, px: 2, py: '6px',
        display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: 11.5,
        color: TOKENS.ink2, flex: '0 0 auto',
      }}>
        <Box>
          Σ total demand, all inks, 12 mth:{' '}
          <Box component="span" sx={{
            fontFamily: MONO, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: TOKENS.ink,
          }}>
            {Math.round(grandTotal).toLocaleString()} kg
          </Box>
        </Box>
        <Box>
          {rows.length.toLocaleString()} rows in view of {leafCount.toLocaleString()} detail lines
        </Box>
        <Box sx={{ color: short.length ? TOKENS.bad : TOKENS.ink2, fontWeight: short.length ? 600 : 400 }}>
          {short.length
            ? `${short.length} ink${short.length === 1 ? '' : 's'} go short inside the horizon`
            : 'no inks go short'}
        </Box>
        {coverage.uncostedSheets > 0 && (
          // The Σ above is a total of what we COULD cost. Saying so beside it,
          // in the same breath, stops it reading as the whole picture.
          <Box sx={{ color: TOKENS.warn, fontWeight: 600 }}>
            Σ covers {(100 - coverage.uncostedPct).toFixed(1)}% of demand
            {' '}({forecast.excluded.length} line
            {forecast.excluded.length === 1 ? '' : 's'} not costed)
          </Box>
        )}
        {/* The shareable state, shown so it is obvious the view IS the URL. It
            can get long once several branches are open, so it clips rather than
            shoving the counts that matter off the bar. */}
        <Box title={`#${hash}`} sx={{
          ml: 'auto', color: TOKENS.ink3, fontFamily: MONO, fontSize: 10.5,
          maxWidth: 460, minWidth: 0, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          state: #{hash}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column header
// ---------------------------------------------------------------------------

function ColumnHeader({ columns, winStart, winEnd, qOpen, onQuarter, onDragStart, onDragOver }: {
  columns: ForecastColumn[];
  winStart: number; winEnd: number;
  qOpen: Record<number, boolean>;
  onQuarter: (q: number) => void;
  onDragStart: (month: number) => void;
  onDragOver: (month: number) => void;
}) {
  const microLabel = {
    fontSize: 11, fontWeight: 600, letterSpacing: '0.5px',
    color: TOKENS.ink3, textTransform: 'uppercase' as const,
  };
  return (
    <Box sx={{ ...gridSx, alignItems: 'end' }}>
      <Box sx={{ ...microLabel, px: '8px', pt: '6px', pb: '5px' }}>
        Ink series · ink · customer · form · FG SKU
      </Box>
      <Box sx={{ ...figSx, ...microLabel, pt: '6px' }}>Opening</Box>
      {columns.map((c) => {
        const inWindow = c.month !== undefined && c.month >= winStart && c.month <= winEnd;
        return (
          <Box
            key={c.key}
            sx={{
              ...figSx, px: '8px', pt: '6px', pb: '5px',
              // Only months are drag targets — a window is a run of months, so
              // dragging over a quarter total has no meaning to express.
              cursor: c.month !== undefined ? 'ew-resize' : 'default',
              bgcolor: c.wide ? WIDE_FILL_STRONG : inWindow ? WINDOW_TINT : 'transparent',
              borderLeft: c.wide ? `1px solid ${RULE}` : 'none',
              borderTop: `2px solid ${inWindow ? TOKENS.accent : 'transparent'}`,
              userSelect: 'none',
            }}
            onMouseDown={c.month !== undefined
              ? (e: React.MouseEvent) => { e.preventDefault(); onDragStart(c.month!); }
              : undefined}
            onMouseEnter={c.month !== undefined ? () => onDragOver(c.month!) : undefined}
          >
            <Box sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 11 }}>{c.label}</Box>
            {c.sub && (
              <Box sx={{
                fontSize: 9, fontWeight: 600, color: TOKENS.ink3,
                letterSpacing: '0.4px', textTransform: 'uppercase',
              }}>
                {c.sub}
              </Box>
            )}
            {c.quarter !== undefined && (
              <Box
                component="button" type="button"
                onClick={() => onQuarter(c.quarter!)}
                sx={{
                  font: 'inherit', fontSize: 9.5, fontWeight: 600, mt: '2px', px: '5px',
                  borderRadius: `${TOKENS.radiusChip}px`, cursor: 'pointer',
                  border: `1px solid ${qOpen[c.quarter] ? TOKENS.accent : RULE}`,
                  bgcolor: qOpen[c.quarter] ? TOKENS.accent : TOKENS.panel,
                  color: qOpen[c.quarter] ? '#fff' : TOKENS.ink3,
                }}
              >
                {qOpen[c.quarter] ? '– mth' : '+ mth'}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Context band — which ink am I inside?
// ---------------------------------------------------------------------------

function ContextBand({ ink, customer, columns, top }: {
  ink: ForecastNode; customer: ForecastNode | null; columns: ForecastColumn[]; top: number;
}) {
  return (
    <Box sx={{
      ...gridSx, position: 'sticky', top: `${top}px`, zIndex: 3, height: CONTEXT_H,
      // The series tint is a translucent token, and a sticky band has rows
      // sliding UNDER it — so it is composited over the panel white rather than
      // set as a colour, or the row beneath reads straight through it.
      bgcolor: TOKENS.panel,
      backgroundImage: `linear-gradient(${BG_SERIES}, ${BG_SERIES})`,
      borderBottom: `1px solid ${TOKENS.ink}`, boxSizing: 'border-box',
    }}>
      <Box sx={{
        display: 'flex', gap: '7px', alignItems: 'center', pl: '22px',
        fontFamily: MONO, fontSize: 11.5, fontWeight: 700, overflow: 'hidden',
      }}>
        <Swatch hex={ink.ink?.hex_colour} size={9} />
        {ink.code}
        <Box component="span" sx={{
          fontFamily: 'inherit', fontWeight: 400, color: TOKENS.ink2, whiteSpace: 'nowrap',
        }}>
          — {ink.name}{customer ? ` · in ${customer.code}` : ''} · {ink.leafCount} lines
        </Box>
      </Box>
      <FigRow values={ink.kg} columns={columns} opts={{ bold: true, strong: true }} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function ForecastRow({ row, columns, onToggle }: {
  row: Row; columns: ForecastColumn[]; onToggle: (key: string) => void;
}) {
  const { node, kind, indent } = row;

  if (kind === 'summary') return <InkSummary node={node} columns={columns} indent={indent} />;

  if (kind === 'sheets') {
    return (
      <Box sx={{
        ...gridSx, height: ROW_H, boxSizing: 'border-box', overflowY: 'hidden',
        borderBottom: `1px solid ${RULE}`, bgcolor: BG_SHEETS,
      }}>
        <Box sx={{
          display: 'flex', gap: '10px', alignItems: 'center',
          pl: `${INDENT * (indent + 1) + 14}px`, fontSize: 10.5, color: TOKENS.ink3,
          overflow: 'hidden',
        }}>
          <Box component="span" sx={{ width: 92, flex: '0 0 auto' }}>Sheet demand</Box>
          {/* The label column clips, so the full derivation — separation and SKU
              description included — is on the row's own title. */}
          <Box component="span" title={leafFullMeta(node.leaf!)} sx={{
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {node.meta}
          </Box>
        </Box>
        <FigRow values={node.sheets} columns={columns} opts={{ sheets: true, dim: true }} />
      </Box>
    );
  }

  const isLeaf = kind === 'leaf';
  const isSeries = kind === 'series';
  const clickable = !isLeaf && !isSeries;
  // Series rows never collapse — they are the permanent skeleton, so their caret
  // is a state indicator, not a control.
  const caret = isLeaf ? '' : isSeries ? '▾' : row.open ? '▾' : '▸';

  return (
    <Box sx={{
      ...gridSx, height: ROW_H, boxSizing: 'border-box', overflowY: 'hidden',
      borderBottom: `1px solid ${RULE}`,
      bgcolor: node.level === 0 ? BG_SERIES : node.level === 1 ? BG_INK : TOKENS.panel,
    }}>
      <Box
        onClick={clickable ? () => onToggle(node.key) : undefined}
        sx={{
          display: 'flex', alignItems: 'center', gap: '7px',
          pl: `${INDENT * indent + 6}px`, overflow: 'hidden',
          cursor: clickable ? 'pointer' : 'default',
        }}
      >
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 9, color: TOKENS.ink3, width: 10, flex: '0 0 auto' }}>
          {caret}
        </Box>
        {node.level === 1 && <Swatch hex={node.ink?.hex_colour} size={9} />}
        <Box component="span" sx={{
          fontFamily: MONO, fontSize: 11.5, fontWeight: node.level <= 1 ? 700 : 600, flex: '0 0 auto',
        }}>
          {node.code}
        </Box>
        <Box component="span" sx={{
          fontSize: 11.5, color: TOKENS.ink2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {node.level === 1 ? `${node.name} — ${node.meta}` : node.name}
        </Box>
        {/* Coverage rides on the FG SKU row, in the same right-hand slot the
            group rows use for their line count — so scanning down the label
            column gives you "how many lines" at every level above the leaf and
            "how heavy is this one" at the leaf. Mono and tabular because it is
            a figure and every figure on this report reads as one. */}
        {isLeaf ? (
          <Box component="span" sx={{
            fontFamily: MONO, fontSize: 10, color: TOKENS.ink3,
            fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', ml: 'auto', pr: '8px',
          }}>
            {leafCoverage(node.leaf!)}
          </Box>
        ) : (
          <Box component="span" sx={{
            fontSize: 10, color: TOKENS.ink3, flex: '0 0 auto', ml: 'auto', pr: '8px',
          }}>
            {node.leafCount} lines
          </Box>
        )}
      </Box>
      <FigRow
        values={node.kg}
        columns={columns}
        opts={{ bold: node.level <= 1, dim: node.level >= 3, strong: node.level <= 1 }}
      />
    </Box>
  );
}

/**
 * The three-line block that closes every expanded ink.
 *
 * Total demand / Delivery / Balance, in the same position and the same order the
 * board forecast puts them — the whole point of the register is that someone who
 * reads board can read this without being taught it.
 */
function InkSummary({ node, columns, indent }: {
  node: ForecastNode; columns: ForecastColumn[]; indent: number;
}) {
  const ink = node.ink!;
  const balance = balanceOf(ink.opening_kg, ink.delivery, node.kg);
  const line = (
    name: string, values: number[], opts: FigRowOpts, borderTop: string,
  ) => (
    <Box key={name} sx={{
      ...gridSx, height: SUMMARY_ROW_H, boxSizing: 'border-box',
      borderTop, bgcolor: BG_INK,
    }}>
      <Box sx={{ fontSize: 11, pl: `${INDENT * indent + 14}px`, fontWeight: 600, color: TOKENS.ink }}>
        {name}
      </Box>
      <FigRow values={values} columns={columns} opts={opts} />
    </Box>
  );
  return (
    <Box>
      {line('Total demand (kg)', node.kg, { bold: true, strong: true }, `2px solid ${EMPHASIS_RULE}`)}
      {line('Delivery (kg)', ink.delivery, {}, `1px solid ${RULE}`)}
      {line('Balance (kg)', balance, { signed: true, bold: true, openingVal: ink.opening_kg }, `1px solid ${RULE}`)}
    </Box>
  );
}
